import { HOTWALLET_ID, HotWalletModule, StellarWalletsKit, WalletNetwork } from "@creit.tech/stellar-wallets-kit";
import {
  Address,
  BASE_FEE,
  Contract,
  Networks,
  Operation,
  rpc as StellarRpc,
  TransactionBuilder,
  nativeToScVal,
} from "@stellar/stellar-sdk";
import Big from "big.js";
import { t } from "i18next";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID } from "@/constants";
import { config } from "@/services/config";
import * as Client from "@/services/contracts/stellar/contract";
import * as TokenClient from "@/services/contracts/stellar/token";
import { formatTokenAmount } from "@/utils";

import { hapiApi } from "../api/hapi";
const { Api } = StellarRpc;

async function getTransactionStatus(hash: string, server: StellarRpc.Server) {
  try {
    const tx = await server.getTransaction(hash);
    return tx;
  } catch (error) {
    console.error("Error fetching transaction:", error);
    throw error;
  }
}

async function waitForTransaction(hash: string, server: StellarRpc.Server, maxAttempts = 15) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await getTransactionStatus(hash, server);
      console.log(result.status, result);
      if (result.status === "SUCCESS") {
        return result;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    } catch (error) {
      console.log("error while waiting for transaction", error);
      throw error;
    }
  }
  throw new Error("Transaction polling timed out");
}

const kit: StellarWalletsKit = new StellarWalletsKit({
  network: WalletNetwork.PUBLIC,
  selectedWalletId: HOTWALLET_ID,
  modules: [new HotWalletModule()],
});

const mintScore = async ({ message, signature, address }: { message: Buffer; signature: Buffer; address: string }) => {
  try {
    ToastService.loading({ text: t("Messages.LoadingAction", { name: "score" }), id: TOAST_ID.MINT });

    let isUpdateMint = false;
    const server = new StellarRpc.Server(config.stellar.rpcUrl);

    const localClient = new Client.Client({
      ...Client.networks.mainnet,
      rpcUrl: config.stellar.rpcUrl,
      allowHttp: true,
    });

    const localTokenClient = new TokenClient.Client({
      networkPassphrase: Networks.PUBLIC,
      contractId: config.stellar.stellarXLM,
      rpcUrl: config.stellar.rpcUrl,
      allowHttp: true,
    });
    const { address: wallet } = await kit.getAddress();
    const contractXLM = new Contract(config.stellar.stellarXLM);

    const prices = await localClient.get_prices();

    const currentFee = isUpdateMint ? prices.result[1] : prices.result[0];

    try {
      const contract = new Contract(config.stellar.attestation);
      const call = contract.call("get_user", new Address(address).toScVal());
      const account = await server.getAccount(wallet);

      const tx = new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: Networks.PUBLIC,
      })
        .addOperation(call)
        .setTimeout(30)
        .build();

      const simulationResult = await server.simulateTransaction(tx);

      if (Api.isSimulationRestore(simulationResult)) {
        const restoreAccount = await server.getAccount(wallet);
        const restoreTx = new TransactionBuilder(restoreAccount, {
          fee: (parseInt(BASE_FEE) + parseInt(simulationResult.restorePreamble.minResourceFee)).toString(),
          networkPassphrase: Networks.PUBLIC,
        })
          .setNetworkPassphrase(Networks.PUBLIC)
          .setSorobanData(simulationResult.restorePreamble.transactionData.build())
          .addOperation(Operation.restoreFootprint({}))
          .setTimeout(30)
          .build();

        const signedXdr = await kit.signTransaction(restoreTx.toEnvelope().toXDR("base64"), {
          networkPassphrase: Networks.PUBLIC,
          address: wallet,
        });
        const signedTx = TransactionBuilder.fromXDR(signedXdr.signedTxXdr, Networks.PUBLIC);

        const txResult = await server.sendTransaction(signedTx);
        ToastService.update({ text: t("Messages.WaitingRestore"), id: TOAST_ID.MINT });

        await waitForTransaction(txResult.hash, server);
      }

      const user = await localClient.get_user({ user: address }, { simulate: true });

      if (user.result.trust_score_record) {
        isUpdateMint = true;
      }
      console.log("user", user, isUpdateMint, user.result);
    } catch (error) {
      console.log("Initial call error:", error);
    }
    const balance = await localTokenClient.balance({ id: wallet });

    if (balance && balance.result && Big(balance.result.toString()).lt(Big(currentFee.toString() ?? 0).add(10000000))) {
      ToastService.update({
        text: t("Messages.MissingBalance", {
          value: formatTokenAmount(Big(currentFee.toString() ?? 0).add(10000000), 7),
          symbol: "XLM",
        }),
        id: TOAST_ID.MINT,
      });
      return false;
    }
    console.log("currentFee", currentFee);
    ToastService.update({ text: t("Messages.CreatingApproveTransaction"), id: TOAST_ID.MINT });
    try {
      const latestLedger = await server.getLatestLedger();
      const expirationLedger = latestLedger.sequence + 20000;
      console.log("expirationLedger", expirationLedger);
      const account = await server.getAccount(wallet);
      const tx = await new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: Networks.PUBLIC,
      })
        .addOperation(
          contractXLM.call(
            "approve",
            new Address(wallet).toScVal(),
            new Address(config.stellar.attestation).toScVal(),
            nativeToScVal(currentFee, { type: "i128" }),
            nativeToScVal(expirationLedger, { type: "u32" }),
          ),
        )
        .setTimeout(60)
        .build();
      console.log(tx.toXDR());
      const preparedTx = await server.prepareTransaction(tx);

      const signedXdr = await kit.signTransaction(preparedTx.toEnvelope().toXDR("base64"), {
        networkPassphrase: Networks.PUBLIC,
        address: wallet,
      });

      console.log(signedXdr.signedTxXdr);
      const signedTx = TransactionBuilder.fromXDR(signedXdr.signedTxXdr, Networks.PUBLIC);
      console.log(signedTx);
      const txResult = await server.sendTransaction(signedTx);
      ToastService.update({ text: t("Messages.WaitingConfirmation"), id: TOAST_ID.MINT });
      console.log("txResult", txResult);
      await waitForTransaction(txResult.hash, server);
    } catch (error) {
      console.log("error while approving attestation fee", error);
      ToastService.update({ text: t("Messages.Error", { action: "creating approve" }), id: TOAST_ID.MINT });
      return false;
    }

    try {
      const account = await server.getAccount(wallet);
      const contractAttestation = new Contract(config.stellar.attestation);
      const tx = await new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: Networks.PUBLIC,
      })
        .addOperation(
          contractAttestation.call(
            "self_attest",
            nativeToScVal(message, { type: "bytes" }),
            nativeToScVal(signature, { type: "bytes" }),
          ),
        )
        .setTimeout(60)
        .build();
      ToastService.update({ text: t("Messages.CreatingAttestationTransaction"), id: TOAST_ID.MINT });

      const preparedTx = await server.prepareTransaction(tx);
      const signedXdr = await kit.signTransaction(preparedTx.toEnvelope().toXDR("base64"), {
        networkPassphrase: Networks.PUBLIC,
        address: wallet,
      });

      const signedTx = TransactionBuilder.fromXDR(signedXdr.signedTxXdr, Networks.PUBLIC);

      console.log("signedTx", signedTx);
      const txResult = await server.sendTransaction(signedTx);
      console.log("txResult", txResult);
      ToastService.loading({ text: t("Messages.WaitingConfirmation"), id: TOAST_ID.MINT });
      await waitForTransaction(txResult.hash, server);
    } catch (error) {
      console.log("error while attesting", error);
      ToastService.update({ text: t("Messages.Error", { action: "creating attestation" }), id: TOAST_ID.MINT });
      return false;
    }
    ToastService.update({
      text: `Trust score is ${!isUpdateMint ? "mint" : "re-mint"}ed`,
      id: TOAST_ID.MINT,
      type: "success",
    });

    await hapiApi.logAttest({ chain: -5, address, method: isUpdateMint ? 1 : 0 });

    return true;
  } catch (error) {
    console.log("error while minting score", error);
    ToastService.clear(TOAST_ID.MINT);
    return false;
  }
};

export default {
  mintScore,
};
